import { useEffect, useState } from "react";
import {
  PanelShell, Badge, Button, TextField, ApiResourceView,
  storageBudgetState, aiProviderIsReady,
  DJSEMApiClient, useApiResource, useApiAction,
  type ShellUserContext,
} from "../index";
import type { DJSEMPlatform, DJSEMCachedLicense, DJSEMComputeCapabilityReport, DJSEMComputeTierPreference } from "../platform";

/** The perpetual-license routes return `{ok:false, error:"<code_string>", reason?}` on failure —
 * a raw string, not the `{code,message}` shape `DJSEMApiResult`'s type declares (and that
 * `mapResultToUiState`/`useApiAction` assume everywhere else). Reading it through that mapper
 * would silently lose the actual code, so this panel reads `result.error` directly instead. */
const LICENSE_ERROR_COPY: Record<string, string> = {
  not_found: "That license key doesn't match any license on file.",
  activation_denied: "This license is already activated on a different machine. Ask support to reset it, or use that machine.",
  // Issue #18: this fires from activateLicense specifically (LICENSE_SIGNING_*_JWK unset on the
  // Worker — see issue #20) — purchasing itself no longer 501s (LICENSE_PRICE_CENTS is real),
  // so the copy must say "activation," not "purchasing," or it misdirects a customer who already
  // paid and is stuck on this exact screen trying to redeem a real key.
  not_configured: "License activation isn't enabled yet — try again shortly, or contact support if this persists.",
  server_misconfigured: "License activation is temporarily unavailable. Try again shortly.",
};
export function licenseErrorMessage(err: unknown): string {
  const code = typeof err === "string" ? err : null;
  if (code && LICENSE_ERROR_COPY[code]) return LICENSE_ERROR_COPY[code];
  if (code) return code;
  return "Something went wrong — please try again.";
}

/**
 * Perpetual desktop license (task #45, Phase E). Desktop-only (platform.license.available is
 * false on web, see platform/index.ts's nullLicenseBridge) — the purchase flow itself (email +
 * tier -> Stripe checkout) is a plain web page since it runs before/without any desktop install
 * or account session; this panel only covers what needs the machine id: activating a key already
 * purchased, and checking/clearing this machine's binding.
 */
function LicensePanel({ client, platform, license, onRefresh }: {
  client: DJSEMApiClient; platform: DJSEMPlatform; license: DJSEMCachedLicense | null; onRefresh: () => void;
}) {
  const [licenseKey, setLicenseKey] = useState("");
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [rechecking, setRechecking] = useState(false);

  if (!platform.license.available) return null;

  const activate = async () => {
    const key = licenseKey.trim();
    if (!key) return;
    setActivating(true);
    setActivateError(null);
    const machineId = await platform.license.getMachineId();
    const res = await client.activateLicense(key, machineId);
    if (res.ok) {
      await platform.license.cacheToken(res.data.token);
      setLicenseKey("");
      onRefresh();
    } else {
      setActivateError(licenseErrorMessage(res.error));
    }
    setActivating(false);
  };

  const recheck = async () => {
    if (!license) return;
    setRechecking(true);
    const res = await client.getLicenseRevocationStatus(license.licenseId);
    if (res.ok && res.data.status !== "paid") await platform.license.clearCached();
    onRefresh();
    setRechecking(false);
  };

  const remove = async () => {
    await platform.license.clearCached();
    onRefresh();
  };

  return (
    <PanelShell title="License" style={{ marginTop: 12 }}>
      {license ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Badge tone="ok">{license.tier}</Badge>
            <span style={{ fontSize: 11, opacity: 0.6 }}>{license.licenseId}</span>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={() => void recheck()} disabled={rechecking}
              style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, cursor: "pointer", background: "transparent", color: "inherit", border: "1px solid currentColor" }}>
              {rechecking ? "Checking…" : "Recheck status"}
            </button>
            <button onClick={() => void remove()}
              style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, cursor: "pointer", background: "transparent", color: "inherit", border: "1px solid currentColor" }}>
              Remove from this machine
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8 }}>
            No perpetual license activated on this machine. Already bought one? Enter the key below.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <TextField value={licenseKey} onChange={setLicenseKey} placeholder="License key" style={{ flex: 1 }} />
            <Button onClick={() => void activate()} disabled={activating || !licenseKey.trim()}>
              {activating ? "Activating…" : "Activate"}
            </Button>
          </div>
          {activateError ? <div style={{ color: "#e5484d", fontSize: 12, marginTop: 6 }}>{activateError}</div> : null}
        </>
      )}
    </PanelShell>
  );
}

const KNOWN_PROVIDERS: Array<{ id: string; label: string }> = [
  { id: "spotify", label: "Spotify" },
  { id: "soundcloud", label: "SoundCloud" },
];

const TIER_LABELS: Record<DJSEMComputeTierPreference, string> = {
  auto: "Auto (best available)",
  cuda_gpu: "NVIDIA GPU (CUDA)",
  apple_silicon_gpu: "Apple Silicon GPU (MPS)",
  cpu: "CPU",
};

/**
 * Compute/hardware tier preference (prompt 149). Local model execution (stem separation, music/
 * voice/symbolic generation) doesn't require a dedicated discrete GPU — CUDA, Apple Silicon
 * (MPS), and CPU are all real fallback-ordered tiers detected by the Electron main process (see
 * @djsem/shared's computeTier.ts + apps/mvp-electron's hardwareProbe.ts). Desktop-only: web has
 * no local GPU/process concept at all, so platform.compute is a null-object there (honest
 * "desktop only" message, never a fake capability report).
 */
function ComputeHardwarePanel({ platform }: { platform: DJSEMPlatform }) {
  const [capability, setCapability] = useState<DJSEMComputeCapabilityReport | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    void platform.compute.getCapability().then((c) => { setCapability(c); setLoaded(true); });
  };
  useEffect(refresh, [platform]);

  if (!platform.compute.available) {
    return (
      <PanelShell title="Compute / Hardware" style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          Desktop-only — local model execution (stem separation, music/voice generation) needs a native
          process the browser doesn't have.
        </div>
      </PanelShell>
    );
  }

  if (!loaded || !capability) {
    return <PanelShell title="Compute / Hardware" style={{ marginTop: 12 }}><div style={{ fontSize: 12, opacity: 0.7 }}>Detecting hardware…</div></PanelShell>;
  }

  const setPreference = async (pref: DJSEMComputeTierPreference) => {
    setSaving(true);
    try { await platform.compute.setPreference(pref); refresh(); }
    finally { setSaving(false); }
  };

  return (
    <PanelShell title="Compute / Hardware" style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        Running on <strong>{TIER_LABELS[capability.tier]}</strong>
        {capability.usedFallback ? <span style={{ marginLeft: 6 }}><Badge tone="warn">fallback</Badge></span> : null}
      </div>
      {capability.warnings.map((w, i) => (
        <div key={i} style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>{w}</div>
      ))}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {(Object.keys(TIER_LABELS) as DJSEMComputeTierPreference[]).map((pref) => {
          const isAvailable = pref === "auto" || capability.availableTiers.includes(pref);
          const isActive = capability.preference === pref;
          return (
            <button
              key={pref}
              disabled={saving || !isAvailable}
              onClick={() => void setPreference(pref)}
              style={{
                fontSize: 11, padding: "4px 8px", borderRadius: 4,
                cursor: isAvailable ? "pointer" : "not-allowed",
                background: "transparent", color: "inherit",
                border: isActive ? "2px solid currentColor" : "1px solid currentColor",
                opacity: isAvailable ? 1 : 0.4,
              }}
            >
              {TIER_LABELS[pref]}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 10, opacity: 0.6, marginTop: 6 }}>
        Detected: {capability.probe.platform}/{capability.probe.arch}
        {capability.availableTiers.includes("cuda_gpu") ? " · CUDA GPU detected" : ""}
        {capability.availableTiers.includes("apple_silicon_gpu") ? " · Apple Silicon GPU detected" : ""}
      </div>
    </PanelShell>
  );
}

/**
 * Connected accounts (prompts 113/114, task #36). Spotify/SoundCloud are real per-user OAuth
 * connections (HMAC-signed state, PKCE for SoundCloud, refresh tokens stored server-side only —
 * see apps/analysis-worker/src/routes/platformOAuth.ts). Connect opens the provider's consent
 * screen in a popup; the popup posts a message back and closes itself on success, so we just
 * listen for that and refetch status rather than polling.
 */
function ConnectedAccountsPanel({ client }: { client: DJSEMApiClient }) {
  const res = useApiResource(() => client.listOAuthConnections(), []);
  const disconnect = useApiAction((provider: string) => client.disconnectOAuth(provider));

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data && typeof e.data === "object" && e.data.djsem === "oauth-done") res.refetch();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [res]);

  const connect = (provider: string) => {
    window.open(client.oauthConnectUrl(provider), "djsem-oauth", "width=480,height=680");
  };

  return (
    <PanelShell title="Connected accounts" style={{ marginTop: 12 }}>
      <ApiResourceView
        resource={res}
        empty={{ title: "No connections yet", detail: "Connect Spotify or SoundCloud to publish sets as playlists." }}
        render={(data) => {
          const connectedByProvider = new Map(data.connected.map((c) => [c.provider, c]));
          return (
            <>
              {KNOWN_PROVIDERS.map(({ id, label }) => {
                const conn = connectedByProvider.get(id);
                return (
                  <div key={id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                    <span>{label}{conn ? <span style={{ fontSize: 10, opacity: 0.6 }}> · connected {conn.grantedAt.slice(0, 10)}</span> : null}</span>
                    {conn ? (
                      <button onClick={() => void disconnect.run(id).then(res.refetch)} disabled={disconnect.pending}
                        style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, cursor: "pointer", background: "transparent", color: "inherit", border: "1px solid currentColor" }}>
                        Disconnect
                      </button>
                    ) : (
                      <Button onClick={() => connect(id)}>Connect</Button>
                    )}
                  </div>
                );
              })}
              {data.unavailable.map((u) => (
                <div key={u.provider} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", opacity: 0.6 }}>
                  <span style={{ textTransform: "capitalize" }}>{u.provider}</span>
                  <Badge tone="neutral">not available</Badge>
                </div>
              ))}
              {data.unavailable.map((u) => (
                <div key={`${u.provider}-reason`} style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>{u.reason}</div>
              ))}
            </>
          );
        }}
      />
    </PanelShell>
  );
}

/**
 * Settings module — the workspace settings screen: storage budget, AI provider readiness,
 * current plan, perpetual desktop license, compute/hardware tier preference, and connected
 * third-party accounts (Spotify/SoundCloud OAuth).
 *
 * @param user - The signed-in user's shell context (used here for their plan).
 * @param client - The API client used for AI provider status and OAuth connections.
 * @param platform - The host platform adapter (used here for the license and compute-tier bridges).
 * @param license - The cached perpetual license, if any, for the license panel.
 * @param onLicenseChanged - Called after activation/recheck/removal so the caller can refresh its own license state.
 * @returns Storage, AI Providers, Plan, License, Compute/Hardware, and Connected Accounts panels.
 */
export function SettingsModule({ user, client, platform, license, onLicenseChanged }: {
  user: ShellUserContext; client: DJSEMApiClient; platform: DJSEMPlatform;
  license: DJSEMCachedLicense | null; onLicenseChanged: () => void;
}) {
  const budget = storageBudgetState({ usedMB: 240, limitMB: 1000 });
  return (
    <>
      <PanelShell title="Storage"><div>{budget.message} — <Badge tone={budget.tone === "ok" ? "ok" : "warn"}>{budget.pct}%</Badge></div></PanelShell>
      <PanelShell title="AI Providers" style={{ marginTop: 12 }}>
        <div>{aiProviderIsReady({ providerId: "stub", label: "Stub", status: "enabled", credentialConfigured: false }) ? "ready" : "no credentials configured"}</div>
      </PanelShell>
      <PanelShell title="Plan" style={{ marginTop: 12 }}><div>{user.plan}</div></PanelShell>
      <LicensePanel client={client} platform={platform} license={license} onRefresh={onLicenseChanged} />
      <ComputeHardwarePanel platform={platform} />
      <ConnectedAccountsPanel client={client} />
    </>
  );
}
